import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router-dom";
// You might need to change the path if this is not in the same directory as BranchHome
import "./BranchManagement.css";
const branchApiKey = process.env.REACT_APP_API_KEY_AUTH_BRANCH;
// IMPORTANT: Replace this placeholder with the actual API endpoint for GET/listing branches.
const BRANCH_API_URL = `https://${branchApiKey}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;
// Note: A real application would typically use a separate GET endpoint for listing branches.

export default function BranchManagement() {
  const auth = useAuth();
  // State for the form data
  const [formData, setFormData] = useState({
    id: "",
    branchName: "",
    email: "",
    location: "",
    contact: "",
  });
  // State to hold the list of existing branches (for table and updates)
  const [branches, setBranches] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // 1. 🛠️ FIX: Wrap fetchBranches in useCallback to make it stable
  const fetchBranches = useCallback(async () => {
    setMessage("Fetching branches...");
    setLoading(true);

    try {
      const res = await fetch(BRANCH_API_URL, {
        method: "GET",
        headers: {
          // Use the access token from the auth context
          Authorization: `Bearer ${auth.user.access_token}`,
        },
      });
      const data = await res.json();
      setBranches(data.branches || []);
      setMessage("Branches loaded successfully.");
    } catch (error) {
      console.error("Error fetching branches:", error);
      setMessage("Error fetching branches. Check console.");
    } finally {
      setLoading(false);
    }
    // Dependency array for useCallback: auth.user.access_token is the only external dependency used inside fetchBranches
  }, [auth.user?.access_token]);

  // 2. 🛠️ FIX: Add fetchBranches to the dependency array
  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]); // ⬅️ Dependency added here to resolve the warning

  // --- Form Handlers ---

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = (branch) => {
    setFormData(branch);
  };

  const handleClear = () => {
    setFormData({
      id: "", // Note: Corrected to lowercase 'id' to match state structure
      branchName: "",
      email: "",
      location: "",
      contact: "",
    });
    setMessage("");
  };

  // --- API Submission Handler (CREATE, UPDATE, DELETE) ---

  // ... existing state and context definitions ...

  const handleSubmit = async (action) => {
    // Input validation for ADD/UPDATE
    if (
      (action === "ADD" || action === "UPDATE") &&
      (!formData.id || !formData.branchName || !formData.email)
    ) {
      setMessage("Branch ID, Name, and Email are required for Add/Update.");
      return;
    }

    // ID validation for all actions (ADD, UPDATE, DELETE)
    if (!formData.id) {
      setMessage(`Branch ID is required for this action: ${action}.`);
      return;
    }

    const isDelete = action === "DELETE";

    let payload;

    if (isDelete) {
      // For delete, only the ID and the action are strictly necessary
      payload = {
        id: formData.id, // Optional: can be included for logging/audit purposes
        action: "DELETE", // Explicitly set action for the backend
      };
    } else {
      // For add/update, send all fields
      payload = {
        ...formData,
        action: action, // Will be "ADD" or "UPDATE"
      };
    }

    // Remove empty strings before sending
    Object.keys(payload).forEach(
      (key) => payload[key] === "" && delete payload[key]
    );

    setLoading(true);
    setMessage(`${action}ing branch...`);

    try {
      // *** Single fetch URL with POST method for all mutations (ADD, UPDATE, DELETE) ***
      const res = await fetch(BRANCH_API_URL, {
        method: "POST", // Use POST for all mutating operations
        headers: {
          "Content-Type": "application/json",
          ...(auth?.user?.access_token
            ? { Authorization: `Bearer ${auth.user.access_token}` }
            : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorBody = await res
          .json()
          .catch(() => ({ message: res.statusText }));
        throw new Error(
          `Failed with status ${res.status}: ${errorBody.message}`
        );
      }

      const result = await res.json();
      setMessage(
        `Branch ${action}ed successfully! Response: ${
          result.message || "Success"
        }`
      );

      // Clear form and refresh list after success
      handleClear();
      fetchBranches();
    } catch (error) {
      console.error(`Error ${action}ing branch:`, error);
      setMessage(`Error: Could not ${action} branch. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="branch-management-container">
      <Link to="/" className="Branch_Back_Btn">
        Back
      </Link>

      <h2>🏦 Branch Administration</h2>

      {/* Status Message Area */}
      {message && (
        <p
          className={`status-message ${
            message.startsWith("Error") ? "error" : "success"
          }`}
        >
          {message}
        </p>
      )}

      {/* Branch Management Form */}
      <div className="form-card">
        <h3>{formData.id ? "Update/Delete Branch" : "Add New Branch"}</h3>

        <div className="form-grid">
          <label>
            Branch ID <span className="required">*</span>
            <input
              type="text"
              name="id"
              value={formData.id}
              onChange={handleChange}
              placeholder="e.g., BR005"
              // Allow typing in id for new branches
              required
            />
          </label>
          <label>
            Branch Name <span className="required">*</span>
            <input
              type="text"
              name="branchName"
              value={formData.branchName}
              onChange={handleChange}
              placeholder="e.g., East Branch"
              required
            />
          </label>
          <label>
            Email <span className="required">*</span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "email",
                    value: e.target.value.toLowerCase(),
                  },
                })
              }
              placeholder="branch@example.com"
              required
            />
          </label>
          <label>
            Location <span className="required">*</span>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="City, State"
            />
          </label>
          <label>
            Contact <span className="required">*</span>
            <input
              type="tel"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              placeholder="10-digit number"
            />
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => handleSubmit("ADD")}
            className="btn primary"
          >
            {loading ? "Adding..." : "Add Branch"}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit("UPDATE")}
            disabled={loading || !formData.id} // Disable UPDATE if ID is empty
            className="btn secondary"
          >
            {loading ? "Updating..." : "Update Branch"}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit("DELETE")}
            disabled={loading || !formData.id} // Disable DELETE if ID is empty
            className="btn danger"
          >
            {loading ? "Deleting..." : "Delete Branch"}
          </button>
          <button type="button" onClick={handleClear} className="btn reset">
            Clear Form
          </button>
        </div>
      </div>

      {/* Branch List Table */}
      <div className="table-card">
        <h3>Existing Branches ({branches.length})</h3>
        {loading && <p>Loading branch data...</p>}
        {!loading && branches.length === 0 ? (
          <p>No branches found. Add one above.</p>
        ) : (
          <table className="branch-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Location</th>
                <th>Contact</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((branch, index) => (
                <tr key={branch.id}>
                  <td>{index + 1}</td>
                  <td>{branch.id}</td>
                  <td>{branch.branchName}</td>
                  <td>{branch.email}</td>
                  <td>{branch.location}</td>
                  <td>{branch.contact}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(branch)}
                      className="btn btn-sm"
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
